import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Play, Plus, ThumbsUp } from 'lucide-react';
import { POSTER_BASE_URL, getMovieVideos, getTVVideos } from '../services/tmdb';

const ContentRow = ({ title, movies, onPlayTrailer }) => {
  const rowRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [hoveredMovie, setHoveredMovie] = useState(null);

  const handleScroll = () => {
    if (rowRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = rowRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    handleScroll();
  }, [movies]);

  const scroll = (direction) => {
    if (rowRef.current) {
      const scrollAmount = rowRef.current.clientWidth * 0.8;
      rowRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  const handlePlayClick = async (movie, e) => {
    e.stopPropagation();
    try {
      const videos = movie.media_type === 'tv' || movie.first_air_date
        ? await getTVVideos(movie.id)
        : await getMovieVideos(movie.id);
      
      const trailer = videos.find(
        (video) => video.type === 'Trailer' && video.site === 'YouTube'
      );
      
      if (trailer && onPlayTrailer) {
        onPlayTrailer(trailer.key, movie.title || movie.name);
      }
    } catch (error) {
      console.error('Error loading trailer:', error);
    }
  };

  if (!movies || movies.length === 0) return null;

  return (
    <div className="px-4 md:px-12 py-6 group relative">
      <h2 className="text-white text-xl md:text-2xl font-bold mb-4">{title}</h2>
      
      <div className="relative group">
        {showLeftArrow && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-0 bottom-0 z-40 w-12 bg-black/50 hover:bg-black/70 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
          >
            <ChevronLeft className="text-white" size={40} />
          </button>
        )}

        <div
          ref={rowRef}
          onScroll={handleScroll}
          className="flex space-x-2 overflow-x-scroll scrollbar-hide scroll-smooth"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {movies.map((movie) => (
            <div
              key={movie.id}
              className="relative min-w-[200px] md:min-w-[250px] transition-transform duration-300 hover:scale-110 hover:z-30 cursor-pointer"
              onMouseEnter={() => setHoveredMovie(movie.id)}
              onMouseLeave={() => setHoveredMovie(null)}
            >
              <img
                src={`${POSTER_BASE_URL}${movie.poster_path || movie.backdrop_path}`}
                alt={movie.title || movie.name}
                className="w-full h-[300px] md:h-[350px] object-cover rounded-md"
              />
              
              {hoveredMovie === movie.id && (
                <div className="absolute inset-0 bg-black/70 rounded-md flex flex-col justify-end p-4 space-y-2 animate-in fade-in duration-200">
                  <h3 className="text-white font-bold text-sm md:text-base line-clamp-1">
                    {movie.title || movie.name}
                  </h3>
                  <div className="flex space-x-2">
                    <button
                      onClick={(e) => handlePlayClick(movie, e)}
                      className="bg-white text-black rounded-full p-2 hover:bg-gray-300 transition-colors"
                    >
                      <Play size={16} fill="black" />
                    </button>
                    <button className="bg-transparent border-2 border-white text-white rounded-full p-2 hover:border-gray-300 transition-colors">
                      <Plus size={16} />
                    </button>
                    <button className="bg-transparent border-2 border-white text-white rounded-full p-2 hover:border-gray-300 transition-colors">
                      <ThumbsUp size={16} />
                    </button>
                  </div>
                  <p className="text-gray-300 text-xs line-clamp-2">
                    {movie.overview}
                  </p>
                  <div className="flex items-center space-x-2 text-xs text-gray-400">
                    <span className="text-green-500 font-semibold">
                      {Math.round(movie.vote_average * 10)}% Match
                    </span>
                    <span>{movie.release_date?.substring(0, 4) || movie.first_air_date?.substring(0, 4)}</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {showRightArrow && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-0 bottom-0 z-40 w-12 bg-black/50 hover:bg-black/70 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
          >
            <ChevronRight className="text-white" size={40} />
          </button>
        )}
      </div>
    </div>
  );
};

export default ContentRow;
